import { useEffect, useState } from "react";
import { getTickets, rateTicket } from "../../firebase/services";
import { TicketStatusBadge, EmptyState, Spinner, Modal, Btn } from "../../components/UI";

const CAT_ICONS = { Plomberie:"🔧","Électricité":"⚡","Serrure / porte":"🔑",Climatisation:"❄️",Autre:"⚙️" };
const STATUS_COLORS = { open:"#e24b4a", prog:"#ef9f27", done:"#639922" };

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `il y a ${Math.round(s/60)} min`;
  if (s < 86400) return `il y a ${Math.round(s/3600)}h`;
  return `il y a ${Math.round(s/86400)}j`;
}

export default function StudentTickets({ roomId, onBack, onNewTicket }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingTicket, setRatingTicket] = useState(null);
  const [hoverStar, setHoverStar] = useState(0);
  const [rating, setRating] = useState(0);

  useEffect(() => {
    if (!roomId) { setLoading(false); return; }
    const unsub = getTickets(data => { setTickets(data); setLoading(false); }, roomId);
    return unsub;
  }, [roomId]);

  const handleRate = async () => {
    if (rating === 0) return;
    await rateTicket(ratingTicket.id, rating);
    setRatingTicket(null);
    setRating(0);
  };

  const statusLabel = { open:"Votre ticket a été reçu", prog:"IAM prend en charge", done:"Problème résolu" };

  if (loading) return <div style={styles.page}><Spinner /></div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>←</button>
        <span style={styles.headerTitle}>Mes tickets</span>
        {roomId && <button style={styles.newBtn} onClick={onNewTicket}>+ Nouveau</button>}
      </div>

      {!roomId && (
        <div style={{ margin:"12px 16px 0", background:"#fffaf0", border:"1px solid #f5d8a0", borderRadius:12, padding:"12px 14px", fontSize:13, color:"#8a4c00", lineHeight:1.5 }}>
          🏠 Votre compte n'est associé à aucune chambre. Contactez IAM Business Group pour pouvoir signaler des problèmes.
        </div>
      )}

      <div style={styles.scroll}>
        {tickets.length === 0 ? (
          <EmptyState icon="🎉" title="Aucun ticket" subtitle="Tout va bien ! Signalez un problème si nécessaire." />
        ) : tickets.map(t => (
          <div key={t.id} style={{ ...styles.card, borderLeft: `4px solid ${STATUS_COLORS[t.status]}` }}>
            <div style={styles.cardHeader}>
              <span style={{ fontSize: 18 }}>{CAT_ICONS[t.category] || "⚙️"}</span>
              <div style={{ flex: 1 }}>
                <p style={styles.cardCat}>{t.category}</p>
                <p style={styles.cardTime}>{timeAgo(t.createdAt)}</p>
              </div>
              <TicketStatusBadge status={t.status} />
            </div>

            <p style={styles.cardDesc}>{t.description}</p>

            <div style={styles.statusNote}>
              <span style={{ color: STATUS_COLORS[t.status], fontSize: 12, marginRight: 4 }}>●</span>
              {statusLabel[t.status]}
            </div>

            {/* Rating prompt for resolved tickets */}
            {t.status === "done" && t.rating === null && (
              <div style={styles.ratePrompt}>
                <p style={{ fontSize: 12, color: "#5a7a9a", margin: "0 0 8px" }}>Le problème est-il bien résolu ?</p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={styles.rateYes} onClick={() => { setRatingTicket(t); setRating(5); }}>
                    👍 Oui, résolu
                  </button>
                  <button style={styles.rateNo} onClick={() => setRatingTicket(t)}>
                    ❌ Pas encore
                  </button>
                </div>
              </div>
            )}

            {t.rating !== null && t.status === "done" && (
              <div style={styles.ratedBadge}>
                {"★".repeat(t.rating)}{"☆".repeat(5 - t.rating)} — Merci pour votre retour
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal open={!!ratingTicket} onClose={() => setRatingTicket(null)} title="Évaluer la résolution">
        {ratingTicket && (
          <div>
            <p style={{ fontSize: 13, color: "#5a7a9a", marginBottom: 16 }}>
              Le problème « {ratingTicket.category} » a-t-il été correctement résolu ?
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
              {[1,2,3,4,5].map(s => (
                <span
                  key={s}
                  style={{ fontSize: 36, cursor: "pointer", color: s <= (hoverStar || rating) ? "#ef9f27" : "#d0dce8" }}
                  onMouseEnter={() => setHoverStar(s)}
                  onMouseLeave={() => setHoverStar(0)}
                  onClick={() => setRating(s)}
                  role="button"
                  aria-label={`${s} étoile${s > 1 ? "s" : ""}`}
                >★</span>
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Btn variant="primary" onClick={handleRate} disabled={rating === 0}>
                Envoyer mon évaluation
              </Btn>
              <Btn variant="ghost" onClick={() => setRatingTicket(null)}>Annuler</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const styles = {
  page: { display: "flex", flexDirection: "column", height: "100%", background: "#f5f8fc", fontFamily: "'Barlow', sans-serif" },
  header: { background: "#1a3a5c", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 },
  backBtn: { background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer", padding: "0 8px 0 0" },
  headerTitle: { flex: 1, fontSize: 15, fontWeight: 700, color: "white" },
  newBtn: { background: "rgba(255,255,255,0.15)", border: "none", color: "white", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  scroll: { flex: 1, overflowY: "auto", padding: "12px 16px 80px" },
  card: { background: "white", borderRadius: "0 14px 14px 0", padding: "14px", marginBottom: 12, borderTop: "0.5px solid #d0dce8", borderRight: "0.5px solid #d0dce8", borderBottom: "0.5px solid #d0dce8" },
  cardHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  cardCat: { fontSize: 14, fontWeight: 700, color: "#1a2a3a", margin: 0 },
  cardTime: { fontSize: 11, color: "#7a90a8", margin: 0 },
  cardDesc: { fontSize: 13, color: "#2a4a6a", lineHeight: 1.5, margin: "0 0 8px" },
  statusNote: { fontSize: 12, color: "#5a7a9a", display: "flex", alignItems: "center" },
  ratePrompt: { marginTop: 10, padding: "10px 12px", background: "#f5f8fc", borderRadius: 10 },
  rateYes: { flex: 1, padding: "8px", borderRadius: 8, background: "#e8f5e0", color: "#2d6a0a", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  rateNo: { flex: 1, padding: "8px", borderRadius: 8, background: "#fdecea", color: "#a32d2d", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  ratedBadge: { marginTop: 8, fontSize: 12, color: "#8a6a00", background: "#fff9e6", padding: "6px 10px", borderRadius: 8 },
};
